
import * as FitFileParser from 'fit-file-parser';
import type { Athlete, TrackPoint, Course } from '../types';

/**
 * The subset of race data needed by parseAndAddAthlete.
 * Combines fields from RaceEvent (id, course) and RaceCategory (athletes, times, duration).
 */
export interface AthleteUploadContext {
    id: string;
    athletes: Athlete[];
    scheduledStartTime?: number;
    startTime: number;
    duration: number;
    course: Course;
}
import { getDistance } from './geoUtils';
import { storageService } from './storageService';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { userService } from './userService';
import { calculateMatchesBurned } from './matchesAnalysis';

const RIDER_COLORS = ['#34d399', '#f87171', '#60a5fa', '#facc15', '#a78bfa'];

/**
 * Helper to extract numeric text from XML elements across various tags and namespace scenarios.
 * Uses childNodes (not children) for maximum XML compatibility, and getElementsByTagNameNS
 * with wildcard namespace to handle default-namespace GPX documents.
 */
const getXmlNumericValue = (node: Element, tagNames: string[]): number | null => {
    const lowerTags = tagNames.map(t => t.toLowerCase());

    // 1. childNodes iteration — most reliable across browsers for XML documents
    for (let i = 0; i < node.childNodes.length; i++) {
        const child = node.childNodes[i];
        if (child.nodeType !== 1) continue; // ELEMENT_NODE only
        const el = child as Element;
        // localName is namespace-unaware; nodeName may include prefix like "gpx:ele"
        let name = (el.localName || el.nodeName || el.tagName || '').toLowerCase();
        // Strip namespace prefix if present (e.g. "ns2:ele" → "ele")
        if (name.includes(':')) name = name.split(':').pop()!;
        if (lowerTags.includes(name)) {
            const txt = el.textContent?.trim();
            if (txt !== undefined && txt !== null && txt !== '') {
                const val = parseFloat(txt);
                if (!isNaN(val)) return val;
            }
        }
    }

    // 2. getElementsByTagNameNS with wildcard namespace — handles any xmlns prefix
    for (const tag of tagNames) {
        try {
            const els = node.getElementsByTagNameNS('*', tag);
            if (els.length > 0 && els[0].textContent) {
                const val = parseFloat(els[0].textContent.trim());
                if (!isNaN(val)) return val;
            }
        } catch (_) { /* ignore */ }
    }

    // 3. getElementsByTagName — works when element has no namespace prefix
    for (const tag of tagNames) {
        try {
            const els = node.getElementsByTagName(tag);
            if (els.length > 0 && els[0].textContent) {
                const val = parseFloat(els[0].textContent.trim());
                if (!isNaN(val)) return val;
            }
        } catch (_) { /* ignore */ }
    }

    // 4. querySelector fallback
    for (const tag of tagNames) {
        try {
            const el = node.querySelector(tag);
            if (el?.textContent) {
                const val = parseFloat(el.textContent.trim());
                if (!isNaN(val)) return val;
            }
        } catch (_) { /* ignore */ }
    }

    return null;
};

export const parseGpxData = (gpxString: string): { track: TrackPoint[], path: [number, number][] } => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(gpxString, "application/xml");
    const trackPoints: TrackPoint[] = [];
    const path: [number, number][] = [];

    let trackpointNodes = Array.from(xmlDoc.getElementsByTagName('trkpt'));
    if (trackpointNodes.length === 0) {
        trackpointNodes = Array.from(xmlDoc.getElementsByTagName('rtept'));
    }
    if (trackpointNodes.length === 0) {
        trackpointNodes = Array.from(xmlDoc.getElementsByTagName('wpt'));
    }
    if (trackpointNodes.length === 0) {
        trackpointNodes = Array.from(xmlDoc.getElementsByTagNameNS('*', 'trkpt')) as Element[];
    }
    if (trackpointNodes.length === 0) {
        trackpointNodes = Array.from(xmlDoc.getElementsByTagNameNS('*', 'rtept')) as Element[];
    }
    if (trackpointNodes.length === 0) {
        trackpointNodes = Array.from(xmlDoc.getElementsByTagNameNS('*', 'wpt')) as Element[];
    }

    if (trackpointNodes.length === 0) {
        return { track: [], path: [] };
    }

    // --- Diagnostic: dump info about the first trackpoint so we can debug elevation issues ---
    if (trackpointNodes.length > 0) {
        const firstNode = trackpointNodes[0];
        const childInfo: string[] = [];
        for (let c = 0; c < firstNode.childNodes.length; c++) {
            const ch = firstNode.childNodes[c];
            if (ch.nodeType === 1) {
                const el = ch as Element;
                const name = el.localName || el.nodeName || el.tagName || '(unknown)';
                const txt = (el.textContent || '').trim().substring(0, 80);
                childInfo.push(`<${name}>${txt}</${name}>`);
            }
        }
        console.log(`[RaceLineIQ GPX Parser] ${trackpointNodes.length} trackpoints found. First point children:`, childInfo);
        const testEle = getXmlNumericValue(firstNode, ['ele', 'elevation', 'alt', 'altitude', 'AltitudeMeters']);
        console.log(`[RaceLineIQ GPX Parser] First point elevation extraction result:`, testEle);
    }

    let lastPointCalc: { lat: number; lon: number; timestamp: number } | null = null;
    let totalDistance = 0;
    let lastElevation: number | null = null;

    for (let i = 0; i < trackpointNodes.length; i++) {
        const node = trackpointNodes[i];
        const lat = parseFloat(node.getAttribute('lat') || '0');
        const lon = parseFloat(node.getAttribute('lon') || '0');
        path.push([lat, lon]);

        // Robust time extraction using childNodes for namespace compatibility
        // Robust time extraction
        let timeContent: string | null = null;
        for (let c = 0; c < node.childNodes.length; c++) {
            const ch = node.childNodes[c];
            if (ch.nodeType === 1) {
                const el = ch as Element;
                let n = (el.localName || el.nodeName || '').toLowerCase();
                if (n.includes(':')) n = n.split(':').pop()!;
                if (n === 'time' && el.textContent) {
                    timeContent = el.textContent.trim();
                    break;
                }
            }
        }
        if (!timeContent) {
            // fallback to querySelector
            timeContent = node.querySelector('time')?.textContent || null;
        }
        const timestamp = timeContent ? new Date(timeContent).getTime() : (Date.now() + i * 1000);
        
        const rawEle = getXmlNumericValue(node, ['ele', 'elevation', 'alt', 'altitude', 'AltitudeMeters']);
        let ele: number = rawEle !== null ? rawEle : (lastElevation !== null ? lastElevation : 0);
        lastElevation = ele;

        let power = null;
        let heartRate = null;

        // Search for extensions using childNodes for namespace compatibility
        let extensionsNode: Element | null = null;
        for (let c = 0; c < node.childNodes.length; c++) {
            const ch = node.childNodes[c];
            if (ch.nodeType === 1) {
                const el = ch as Element;
                let n = (el.localName || el.nodeName || '').toLowerCase();
                if (n.includes(':')) n = n.split(':').pop()!;
                if (n === 'extensions') {
                    extensionsNode = el;
                    break;
                }
            }
        }
        if (!extensionsNode) {
            extensionsNode = node.querySelector('extensions');
        }

        if (extensionsNode) {
            for (const childNode of Array.from(extensionsNode.childNodes)) {
                if (childNode.nodeType !== 1) continue;
                const element = childNode as Element;

                if (element.localName === 'power' && element.textContent) {
                    power = parseInt(element.textContent, 10);
                }

                if (element.localName === 'TrackPointExtension') {
                    for (const tpxChild of Array.from(element.childNodes)) {
                        if (tpxChild.nodeType !== 1) continue;
                        const tpxElement = tpxChild as Element;

                        if (tpxElement.localName === 'hr' && tpxElement.textContent) {
                            heartRate = parseInt(tpxElement.textContent, 10);
                        }
                    }
                }
            }
        }

        let distanceIncrement = 0;
        let speed = 0;

        if (lastPointCalc) {
            distanceIncrement = getDistance(lastPointCalc.lat, lastPointCalc.lon, lat, lon);
            const timeIncrementSeconds = (timestamp - lastPointCalc.timestamp) / 1000;
            if (timeIncrementSeconds > 0) {
                speed = (distanceIncrement / timeIncrementSeconds) * 3.6; // speed in km/h
            }
        }
        totalDistance += distanceIncrement;

        const currentPoint: TrackPoint = {
            lat,
            lon,
            elevation: ele,
            timestamp: timestamp,
            distance: totalDistance,
            speed,
            power,
            heartRate,
        };

        trackPoints.push(currentPoint);
        lastPointCalc = { lat, lon, timestamp };
    }

    // --- Diagnostic: warn if all elevations are 0 ---
    if (trackPoints.length > 0) {
        const hasAnyElevation = trackPoints.some(p => p.elevation !== 0);
        if (!hasAnyElevation) {
            console.warn(
                `%c[RaceLineIQ GPX Parser] WARNING: All ${trackPoints.length} trackpoints have elevation = 0. ` +
                `The GPX file may not contain <ele> tags. ` +
                `Check the browser console output above for "First point children" to confirm.`,
                'color: #f59e0b; font-weight: bold; background: #1f2937; padding: 4px 8px; border-radius: 4px;'
            );
        } else {
            const elevRange = {
                min: Math.min(...trackPoints.map(p => p.elevation)),
                max: Math.max(...trackPoints.map(p => p.elevation)),
            };
            let totalGain = 0;
            for (let i = 1; i < trackPoints.length; i++) {
                const diff = trackPoints[i].elevation - trackPoints[i - 1].elevation;
                if (diff > 0) totalGain += diff;
            }
            console.log(
                `[RaceLineIQ GPX Parser] Elevation extracted: min=${elevRange.min.toFixed(1)}m, ` +
                `max=${elevRange.max.toFixed(1)}m, totalGain=${totalGain.toFixed(0)}m (${Math.round(totalGain * 3.28084)}ft)`
            );
        }
    }

    return { track: trackPoints, path };
};

export const parseTcxData = (tcxString: string): { track: TrackPoint[], path: [number, number][] } => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(tcxString, "application/xml");
    const trackPoints: TrackPoint[] = [];
    const path: [number, number][] = [];

    let trackpointNodes = Array.from(xmlDoc.getElementsByTagName('Trackpoint'));
    if (trackpointNodes.length === 0) {
        trackpointNodes = Array.from(xmlDoc.getElementsByTagNameNS('*', 'Trackpoint')) as Element[];
    }
    if (trackpointNodes.length === 0) {
        return { track: [], path: [] };
    }

    let lastPointCalc: { lat: number; lon: number; timestamp: number } | null = null;
    let totalDistance = 0;
    let lastElevation: number | null = null;

    for (const node of Array.from(trackpointNodes)) {
        const timeStr = node.querySelector('Time')?.textContent;
        const latNode = node.querySelector('LatitudeDegrees') || node.getElementsByTagName('LatitudeDegrees')[0];
        const lonNode = node.querySelector('LongitudeDegrees') || node.getElementsByTagName('LongitudeDegrees')[0];

        if (!latNode?.textContent || !lonNode?.textContent) continue;

        const lat = parseFloat(latNode.textContent);
        const lon = parseFloat(lonNode.textContent);
        path.push([lat, lon]);

        const timestamp = timeStr ? new Date(timeStr).getTime() : (Date.now() + trackPoints.length * 1000);
        
        const rawEle = getXmlNumericValue(node, ['AltitudeMeters', 'ele', 'elevation', 'alt', 'altitude']);
        let ele: number = rawEle !== null ? rawEle : (lastElevation !== null ? lastElevation : 0);
        lastElevation = ele;

        const heartRateVal = node.querySelector('HeartRateBpm Value')?.textContent;
        const powerVal = node.querySelector('Watts')?.textContent;

        const heartRate = heartRateVal ? parseInt(heartRateVal, 10) : null;
        const power = powerVal ? parseInt(powerVal, 10) : null;

        let distanceIncrement = 0;
        let speed = 0;

        if (lastPointCalc) {
            distanceIncrement = getDistance(lastPointCalc.lat, lastPointCalc.lon, lat, lon);
            const timeIncrementSeconds = (timestamp - lastPointCalc.timestamp) / 1000;
            if (timeIncrementSeconds > 0) {
                speed = (distanceIncrement / timeIncrementSeconds) * 3.6; // speed in km/h
            }
        }
        totalDistance += distanceIncrement;

        const currentPoint: TrackPoint = {
            lat,
            lon,
            elevation: ele,
            timestamp: timestamp,
            distance: totalDistance,
            speed,
            power,
            heartRate,
        };

        trackPoints.push(currentPoint);
        lastPointCalc = { lat, lon, timestamp };
    }

    return { track: trackPoints, path };
};

export const parseFitData = (fitBuffer: ArrayBuffer): Promise<{ track: TrackPoint[], path: [number, number][] }> => {
    return new Promise((resolve, reject) => {
        const fitParser = new FitFileParser.default({
            force: true,
            speedUnit: 'km/h',
            lengthUnit: 'm',
            temperatureUnit: 'celsius',
            elapsedRecordField: true,
        });

        fitParser.parse(fitBuffer, (error: string | undefined, data: any) => {
            if (error) {
                return reject(new Error(`FIT Parse Error: ${error}`));
            }

            const records = data.records;
            if (!records || records.length === 0) {
                return resolve({ track: [], path: [] });
            }

            const trackPoints: TrackPoint[] = [];
            const path: [number, number][] = [];

            let lastPointCalc: { lat: number; lon: number; timestamp: number } | null = null;
            let lastElevation: number | null = null;

            for (const record of records) {
                if (record.position_lat != null && record.position_long != null) {
                    const lat = record.position_lat;
                    const lon = record.position_long;
                    const timestamp = new Date(record.timestamp).getTime();

                    path.push([lat, lon]);

                    let speed = record.speed;
                    if (speed !== undefined && speed !== null && speed > 0) {
                        speed = speed * 3.6; // Convert FIT m/s to km/h
                    } else if (lastPointCalc) {
                        const dist = getDistance(lastPointCalc.lat, lastPointCalc.lon, lat, lon);
                        const timeDiff = (timestamp - lastPointCalc.timestamp) / 1000;
                        if (timeDiff > 0) {
                            speed = (dist / timeDiff) * 3.6; // Convert m/s to km/h
                        }
                    }

                    const rawEle = record.enhanced_altitude ?? record.altitude ?? null;
                    let ele: number = rawEle !== null ? rawEle : (lastElevation !== null ? lastElevation : 0);
                    lastElevation = ele;

                    trackPoints.push({
                        lat,
                        lon,
                        timestamp: timestamp,
                        elevation: ele,
                        distance: record.distance ?? 0,
                        speed: speed ?? 0,
                        power: record.power ?? null,
                        heartRate: record.heart_rate ?? null,
                    });

                    lastPointCalc = { lat, lon, timestamp };
                }
            }
            resolve({ track: trackPoints, path });
        });
    });
};

/**
 * Utility function to find the last index of an element in an array that satisfies the predicate.
 * Needed for compatibility as Array.prototype.findLastIndex is not universally available.
 */
const findLastIndex = <T>(arr: T[], predicate: (value: T, index: number, obj: T[]) => boolean): number => {
    for (let i = arr.length - 1; i >= 0; i--) {
        if (predicate(arr[i], i, arr)) {
            return i;
        }
    }
    return -1;
};

/**
 * Trims a track to a given start and end time.
 * It includes one point just outside the window on each end to allow for interpolation.
 * @param track The original array of track points.
 * @param startTime The start of the time window (timestamp).
 * @param endTime The end of the time window (timestamp).
 * @returns A new, trimmed array of track points.
 */
const trimTrack = (track: TrackPoint[], startTime: number, endTime: number): TrackPoint[] => {
    if (!track || track.length === 0) {
        return [];
    }

    const startIndex = track.findIndex(p => p.timestamp >= startTime);
    const endIndex = findLastIndex(track, p => p.timestamp <= endTime);

    // If the track is completely outside the race window, return empty.
    if (startIndex === -1 || endIndex === -1 || startIndex > endIndex) {
        return [];
    }

    // Include one point before the start and one point after the end for context.
    const boundedStartIndex = Math.max(0, startIndex - 1);
    const boundedEndIndex = Math.min(track.length - 1, endIndex + 1);

    return track.slice(boundedStartIndex, boundedEndIndex + 1);
};

/**
 * Reduces the number of points in a track by ensuring a minimum time interval between them.
 * This is a simple but effective down-sampling method to reduce data size.
 * @param track The original array of track points.
 * @param intervalSeconds The minimum time in seconds between points in the output track.
 * @returns A new, smaller array of track points.
 */
const downsampleTrack = (track: TrackPoint[], intervalSeconds: number): TrackPoint[] => {
    if (track.length < 2 || intervalSeconds <= 0) {
        return track;
    }

    const downsampled: TrackPoint[] = [track[0]];
    let lastPoint = track[0];

    for (let i = 1; i < track.length; i++) {
        const currentPoint = track[i];
        const timeDiff = (currentPoint.timestamp - lastPoint.timestamp) / 1000;

        if (timeDiff >= intervalSeconds) {
            downsampled.push(currentPoint);
            lastPoint = currentPoint;
        }
    }

    // Ensure the very last point is always included to maintain the full duration.
    const lastOriginalPoint = track[track.length - 1];
    if (lastPoint.timestamp !== lastOriginalPoint.timestamp) {
        downsampled.push(lastOriginalPoint);
    }

    return downsampled;
};

/**
 * Creates a clean GPX string from filtered track points.
 * Used to upload sanitized/trimmed data to storage.
 */
const createGpxFromTrack = (track: TrackPoint[], name: string): string => {
    const header = `<?xml version="1.0" encoding="UTF-8"?>
<gpx creator="RaceLineIQ" version="1.1" 
 xmlns="http://www.topografix.com/GPX/1/1" 
 xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" 
 xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd http://www.garmin.com/xmlschemas/TrackPointExtension/v1 http://www.garmin.com/xmlschemas/TrackPointExtensionv1.xsd">
 <metadata>
  <time>${new Date().toISOString()}</time>
 </metadata>
 <trk>
  <name>${name}</name>
  <trkseg>`;

    const footer = `
  </trkseg>
 </trk>
</gpx>`;

    const points = track.map(p => {
        const timeStr = new Date(p.timestamp).toISOString();
        let ext = '';
        if (p.power !== null || p.heartRate !== null) {
            ext = `
    <extensions>
     ${p.power !== null ? `<power>${Math.round(p.power)}</power>` : ''}
     <gpxtpx:TrackPointExtension xmlns:gpxtpx="http://www.garmin.com/xmlschemas/TrackPointExtension/v1">
      ${p.heartRate !== null ? `<gpxtpx:hr>${Math.round(p.heartRate)}</gpxtpx:hr>` : ''}
     </gpxtpx:TrackPointExtension>
    </extensions>`;
        }

        return `
   <trkpt lat="${p.lat.toFixed(7)}" lon="${p.lon.toFixed(7)}">
    <ele>${p.elevation.toFixed(1)}</ele>
    <time>${timeStr}</time>${ext}
   </trkpt>`;
    }).join('');

    return header + points + footer;
};



// New helper to parse directly from a fetched URL
export const parseTrackFromUrl = async (fileUrl: string): Promise<{ track: TrackPoint[], path: [number, number][] }> => {
    try {
        // Use CORS mode and NO-CACHE to ensure we don't hit browser cached failed requests
        const response = await fetch(fileUrl, { mode: 'cors', cache: 'no-cache' });

        if (!response.ok) {
            throw new Error(`HTTP fetch error: ${response.status} ${response.statusText}. CORS configuration may be missing on bucket.`);
        }

        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();

        // Detect file type from URL or assume fit if not clear, or check headers?
        // Simplified: Try to detect from extension in URL
        const lowerUrl = fileUrl.toLowerCase();

        if (lowerUrl.includes('.gpx')) {
            const text = new TextDecoder().decode(arrayBuffer);
            return parseGpxData(text);
        } else if (lowerUrl.includes('.tcx')) {
            const text = new TextDecoder().decode(arrayBuffer);
            return parseTcxData(text);
        } else {
            // Default to FIT
            return parseFitData(arrayBuffer);
        }
    } catch (e) {
        // IMPROVED ERROR HANDLING FOR CORS
        if (e instanceof TypeError && e.message.includes("Failed to fetch")) {
            console.warn(
                "%c[CORS ERROR] It looks like the Firebase Storage bucket is blocking the request.\n" +
                "To fix this, you need to configure CORS on your bucket.\n" +
                "1. Ensure 'cors.json' is in your project root.\n" +
                "2. Run: gsutil cors set cors.json gs://<your-bucket-name>",
                "color: orange; font-weight: bold; background: #222; padding: 4px;"
            );
        }
        console.error("Error parsing track from URL:", e);
        throw e;
    }
};


/**
 * Determines the race start time by finding a "consensus" among athlete start points.
 * It looks for the largest cluster of athletes who started within a specific time
 * and distance of each other.
 * @param athletes - The list of all athletes in the race.
 * @param scheduledStartTime - The fallback start time from the race metadata.
 * @returns The timestamp of the consensus start time, or 0 if one cannot be determined.
 */
/**
 * Helper to find an athlete's race launch timestamp (transition from staging to active racing).
 */
const getAthleteLaunchTimestamp = (track: TrackPoint[], courseStart?: [number, number]): number => {
    if (!track || track.length === 0) return 0;

    if (courseStart && (courseStart[0] !== 0 || courseStart[1] !== 0)) {
        // Find if and when athlete first arrives at or near the course start line (e.g. within 250m)
        let firstArrivalIdx = -1;
        for (let i = 0; i < track.length; i++) {
            if (getDistance(track[i].lat, track[i].lon, courseStart[0], courseStart[1]) < 250) {
                firstArrivalIdx = i;
                break;
            }
        }

        if (firstArrivalIdx !== -1) {
            // Scan forward from arrival up to 30 mins for the rollout surge away from start
            const scanEnd = Math.min(track.length, firstArrivalIdx + 1800);
            for (let i = firstArrivalIdx; i < scanEnd; i++) {
                const pt = track[i];
                const distToStart = getDistance(pt.lat, pt.lon, courseStart[0], courseStart[1]);
                if (distToStart > 50) {
                    const hasPowerSurge = pt.power !== null && pt.power >= 130;
                    const hasSpeedSurge = pt.speed >= 12;
                    if (hasPowerSurge || hasSpeedSurge) {
                        return pt.timestamp;
                    }
                }
            }
            return track[firstArrivalIdx].timestamp;
        }
    }

    // Fallback if no courseStart or spatial logic failed
    let searchLimit = Math.min(track.length, 1800);
    for (let i = 0; i < searchLimit; i++) {
        const pt = track[i];
        const hasPowerSurge = pt.power !== null && pt.power >= 150;
        const hasSpeedSurge = pt.speed >= 15;

        if (hasPowerSurge || hasSpeedSurge) {
            return pt.timestamp;
        }
    }

    return track[0].timestamp;
};

/**
 * Determines the consensus start time by analyzing multi-sensor telemetry launch points across athletes.
 * @param athletes - The list of all athletes in the race.
 * @param scheduledStartTime - The fallback start time from the race metadata.
 * @returns The timestamp of the consensus start time, or 0 if one cannot be determined.
 */
const findConsensusStartTime = (athletes: Athlete[], scheduledStartTime?: number, courseStart?: [number, number]): number => {
    if (!athletes || athletes.length === 0) return scheduledStartTime || 0;

    // 1. Extract multi-sensor launch timestamps for all athletes
    const launchPoints = athletes.map(a => {
        if (!a.track || a.track.length === 0) return null;
        const launchTs = getAthleteLaunchTimestamp(a.track, courseStart);
        const firstPt = a.track.find(p => p.timestamp >= launchTs) || a.track[0];

        return {
            athleteId: a.id,
            timestamp: launchTs,
            lat: firstPt.lat,
            lon: firstPt.lon,
        };
    }).filter(p => p !== null) as { athleteId: string; timestamp: number; lat: number; lon: number }[];

    if (launchPoints.length === 0) return scheduledStartTime || 0;

    // Single athlete fallback
    if (launchPoints.length === 1) {
        return launchPoints[0].timestamp;
    }

    const TIME_THRESHOLD_MS = 3 * 60 * 1000; // 3 minutes
    const DISTANCE_THRESHOLD_M = 100; // 100 meters

    let largestCluster: typeof launchPoints = [];

    for (const p1 of launchPoints) {
        const currentCluster = launchPoints.filter(p2 => {
            if (p1.athleteId === p2.athleteId) return true;

            const timeDiff = Math.abs(p1.timestamp - p2.timestamp);
            const dist = getDistance(p1.lat, p1.lon, p2.lat, p2.lon);

            return timeDiff <= TIME_THRESHOLD_MS && dist <= DISTANCE_THRESHOLD_M;
        });

        if (currentCluster.length > largestCluster.length) {
            largestCluster = currentCluster;
        } else if (currentCluster.length === largestCluster.length && largestCluster.length > 0) {
            const currentClusterStartTime = Math.min(...currentCluster.map(p => p.timestamp));
            const largestClusterStartTime = Math.min(...largestCluster.map(p => p.timestamp));
            if (currentClusterStartTime < largestClusterStartTime) {
                largestCluster = currentCluster;
            }
        }
    }

    if (largestCluster.length === 0) {
        return Math.min(...launchPoints.map(p => p.timestamp));
    }

    return Math.min(...largestCluster.map(p => p.timestamp));
};

/**
 * Determines a consensus start or finish point by clustering athlete coordinates.
 * @param athletes List of all athletes in the race.
 * @param pointType "start" or "finish" to determine which end of the track to analyze.
 * @returns A coordinate tuple [lat, lon] for the center of the largest cluster, or null.
 */
const findConsensusCoursePoint = (athletes: Athlete[], pointType: 'start' | 'finish'): [number, number] | null => {
    const DISTANCE_THRESHOLD_M = 100; // Use a larger radius for start/finish locations.

    const points = athletes.map(a => {
        if (!a.track || a.track.length === 0) return null;
        const point = pointType === 'start' ? a.track[0] : a.track[a.track.length - 1];
        return {
            id: a.id,
            lat: point.lat,
            lon: point.lon,
        };
    }).filter(p => p !== null) as { id: string; lat: number; lon: number }[];

    if (points.length === 0) return null;
    if (points.length === 1) return [points[0].lat, points[0].lon];

    let largestCluster: typeof points = [];

    // First, map each point to its cluster of neighbors.
    const allClusters = points.map(p1 =>
        points.filter(p2 => {
            const dist = getDistance(p1.lat, p1.lon, p2.lat, p2.lon);
            return dist <= DISTANCE_THRESHOLD_M;
        })
    );

    // Then, find the largest cluster from all the generated clusters.
    if (allClusters.length > 0) {
        largestCluster = allClusters.reduce((maxCluster, currentCluster) =>
            currentCluster.length > maxCluster.length ? currentCluster : maxCluster
        );
    }

    if (largestCluster.length === 0) {
        // Fallback to the first athlete's point if no cluster is found
        return [points[0].lat, points[0].lon];
    }

    // Calculate the centroid of the largest cluster
    const totalLat = largestCluster.reduce((sum, p) => sum + p.lat, 0);
    const totalLon = largestCluster.reduce((sum, p) => sum + p.lon, 0);
    const centroid: [number, number] = [totalLat / largestCluster.length, totalLon / largestCluster.length];

    return centroid;
};

/**
 * Calculates a consensus duration based on the 90th percentile finish time of all athletes.
 * This ensures we capture the back of the pack without including extreme outliers (e.g., forgot to turn off Garmin).
 * @param athletes List of all athletes in the race.
 * @param raceStartTime The determined start time of the race.
 * @returns The calculated duration in milliseconds.
 */
const calculateConsensusDuration = (athletes: Athlete[], raceStartTime: number): number => {
    if (athletes.length === 0 || raceStartTime === 0) return 0;

    const finishTimes = athletes
        .map(a => (a.track && a.track.length > 0 ? a.track[a.track.length - 1].timestamp : 0))
        .filter(t => t > raceStartTime) // Only consider finishes after the start
        .sort((a, b) => a - b);

    if (finishTimes.length === 0) return 0;

    // Use 90th percentile to include almost all riders, cutting off only extreme outliers
    const index = Math.floor(finishTimes.length * 0.90);
    const p90FinishTime = finishTimes[Math.min(index, finishTimes.length - 1)];

    return Math.max(0, p90FinishTime - raceStartTime);
};

/**
 * Helper to calculate current speed in MPH for a track point.
 */
const getSpeedMph = (pt: TrackPoint, nextPt?: TrackPoint): number => {
    if (nextPt) {
        const distMeters = getDistance(pt.lat, pt.lon, nextPt.lat, nextPt.lon);
        const timeSec = (nextPt.timestamp - pt.timestamp) / 1000;
        if (timeSec > 0) {
            return (distMeters / timeSec) * 2.23694; // m/s to mph
        }
    }
    const rawSpeed = pt.speed ?? 0;
    // TrackPoint.speed is stored in km/h -> convert to mph (* 0.621371)
    return rawSpeed * 0.621371;
};

/**
 * Smart detection for when an athlete finishes their race.
 * Tracks all start/finish line crossings from gun start to end of file.
 * Identifies the LAST active lap crossing (> 10 mph) and adds a 15-second rollout buffer.
 */
const findAthleteFinishTime = (track: TrackPoint[], raceStartTime: number, _scheduledDuration: number, finishLine: [number, number] | undefined): number => {
    if (!track || track.length === 0) return 0;

    const lastTrackPt = track[track.length - 1];

    if (!finishLine || (finishLine[0] === 0 && finishLine[1] === 0)) {
        return lastTrackPt.timestamp;
    }

    const FINISH_RADIUS_METERS = 60; // 60m radius for crossing start/finish line
    const crossings: { timestamp: number; speedMph: number }[] = [];
    let hasLeftFinish = true;

    for (let i = 0; i < track.length; i++) {
        const p = track[i];
        if (p.timestamp < raceStartTime) continue;

        const distToFinish = getDistance(p.lat, p.lon, finishLine[0], finishLine[1]);

        if (distToFinish > FINISH_RADIUS_METERS) {
            hasLeftFinish = true;
        } else if (hasLeftFinish && distToFinish <= FINISH_RADIUS_METERS) {
            const nextPt = track[i + 1];
            const speedMph = getSpeedMph(p, nextPt);
            crossings.push({ timestamp: p.timestamp, speedMph });
            hasLeftFinish = false; // Debounce until rider leaves 60m radius
        }
    }

    if (crossings.length === 0) {
        return lastTrackPt.timestamp;
    }

    // Filter for crossings where rider was traveling at race speed (> 10 mph)
    const racingCrossings = crossings.filter(c => c.speedMph >= 10);
    const lastRacingCrossing = racingCrossings.length > 0
        ? racingCrossings[racingCrossings.length - 1]
        : crossings[crossings.length - 1];

    // Add 15-second rollout buffer after the final finish line crossing
    const finishTimestamp = lastRacingCrossing.timestamp + (15 * 1000);

    return Math.min(finishTimestamp, lastTrackPt.timestamp);
};

export const parseAndAddAthlete = (
    currentRaceData: AthleteUploadContext, 
    file: File, 
    currentUserId?: string, 
    athleteDisplayName?: string, 
    athletePhotoUrl?: string,
    athleteProfile?: { weightKg?: number; heightCm?: number; bikeWeightKg?: number }
): Promise<AthleteUploadContext> => {
    return new Promise(async (resolve, reject) => {
        try {
            const fileNameLower = file.name.toLowerCase();
            const isGpx = fileNameLower.endsWith('.gpx');
            const isTcx = fileNameLower.endsWith('.tcx');
            const isFit = fileNameLower.endsWith('.fit');

            if (!isGpx && !isTcx && !isFit) {
                throw new Error('Unsupported file type. Please upload a .gpx, .tcx, or .fit file.');
            }

            // 1. Read and Parse the new file locally
            const arrayBuffer = await file.arrayBuffer();
            let newTrack: TrackPoint[] = [];
            let newPath: [number, number][] = [];

            if (isFit) {
                const result = await parseFitData(arrayBuffer);
                newTrack = result.track;
                newPath = result.path;
            } else {
                const text = new TextDecoder().decode(arrayBuffer);
                const result = isTcx ? parseTcxData(text) : parseGpxData(text);
                newTrack = result.track;
                newPath = result.path;
            }

            if (newTrack.length === 0) {
                throw new Error('File contains no valid track data.');
            }

            // --- ATHLETE CREATION ---
            // Create a temporary athlete object with the full, untrimmed track for now.
            const newAthleteName = athleteDisplayName || file.name.replace(/\.(gpx|tcx|fit)$/i, '').substring(0, 15);
            const newColorIndex = currentRaceData.athletes.length % RIDER_COLORS.length;

            const newAthlete: Athlete = {
                id: `athlete-${Date.now()}`,
                name: newAthleteName,
                color: RIDER_COLORS[newColorIndex],
                track: newTrack, // Full track for now, will be trimmed later.
                weight: athleteProfile?.weightKg || 75,
                height: athleteProfile?.heightCm,
                bikeWeight: athleteProfile?.bikeWeightKg,
                userId: currentUserId,
                photoUrl: athletePhotoUrl
            };

            const updatedAthletes = [...currentRaceData.athletes, newAthlete];

            // --- RACE START TIME & DURATION CALCULATION (THE NEW LOGIC) ---
            // Find the consensus start time based on the new, complete list of athletes.
            const raceStartTime = findConsensusStartTime(updatedAthletes, currentRaceData.scheduledStartTime || currentRaceData.startTime, currentRaceData.course?.start);

            // If duration wasn't manually set, calculate it based on the race window.
            let duration = currentRaceData.duration;
            if (duration === 0) {
                duration = calculateConsensusDuration(updatedAthletes, raceStartTime);
            }

            // Fetch user profile early to get FTP for match calculations
            let userFtp = 0;
            if (currentUserId) {
                try {
                    const userProfile = await userService.getUserProfile(currentUserId);
                    userFtp = userProfile?.ftp || 0;
                } catch (e) { console.error("Failed to fetch profile for match calculation", e); }
            }

            // --- SMART FILTERING & TRIMMING ---
            // Instead of a hard cutoff at `raceStartTime + duration`, we determine the actual finish line crossing.

            const existingCourse = currentRaceData.course;
            let finishLine: [number, number] | undefined;

            // If course has a start/finish coordinate visited by the athlete track (< 1km), use it.
            if (existingCourse?.start && (existingCourse.start[0] !== 0 || existingCourse.start[1] !== 0) && newTrack.length > 0) {
                const visitsStart = newTrack.some(pt => getDistance(existingCourse.start[0], existingCourse.start[1], pt.lat, pt.lon) < 1000);
                if (visitsStart) {
                    finishLine = (existingCourse.finish && (existingCourse.finish[0] !== 0 || existingCourse.finish[1] !== 0))
                        ? existingCourse.finish
                        : existingCourse.start;
                }
            }

            // Fallback: Use the rider's launch/start location as the finish line for circuit races
            if (!finishLine && newTrack.length > 0) {
                finishLine = [newTrack[0].lat, newTrack[0].lon];
            }

            const finalAthletes: Athlete[] = updatedAthletes.map(athlete => {
                if (!athlete.track || athlete.track.length === 0) {
                    return athlete;
                }

                const individualStart = getAthleteLaunchTimestamp(athlete.track, existingCourse?.start);
                const athleteFinishTime = findAthleteFinishTime(athlete.track, individualStart, duration, finishLine);
                const trimmedTrack = trimTrack(athlete.track, individualStart, athleteFinishTime);

                let matchEvents = athlete.matchEvents;
                if (athlete.id === newAthlete.id && userFtp > 0) {
                    matchEvents = calculateMatchesBurned(trimmedTrack, userFtp);
                }

                return { ...athlete, track: trimmedTrack, matchEvents };
            }).filter(athlete => {
                // Only remove the NEW athlete if their track is outside the window.
                // Keep existing athletes to prevent array shrinking, which violates Firestore security rules.
                if (athlete.id === newAthlete.id) return athlete.track && athlete.track.length > 0;
                return true;
            });

            const newAthleteFinal = finalAthletes.find(a => a.id === newAthlete.id);
            if (!newAthleteFinal) {
                // This can happen if the new athlete's entire file is outside the consensus race window.
                throw new Error("The uploaded file's data falls completely outside the consensus race time and could not be added.");
            }

            // --- FILE SANITIZATION & UPLOAD ---
            // Downsample the final track to a 1-second resolution to save space.
            const downsampledTrack = downsampleTrack(newAthleteFinal.track, 1);
            const sanitizedGpx = createGpxFromTrack(downsampledTrack, newAthleteFinal.name);
            const sanitizedBlob = new Blob([sanitizedGpx], { type: 'application/gpx+xml' });
            const sanitizedFile = new File([sanitizedBlob], `${file.name.replace(/\.[^/.]+$/, "")}_clean.gpx`, { type: 'application/gpx+xml' });

            let fileUrl: string | undefined;
            let rawFileUrl: string | undefined;
            if (currentRaceData.id && currentUserId) {
                try {
                    const ext = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')) : '';
                    const rawFile = new File([file], `${file.name.replace(/\.[^/.]+$/, "")}_raw${ext}`, { type: file.type });

                    const [cleanUrl, rawUrl] = await Promise.all([
                        storageService.uploadRaceFile(currentRaceData.id, currentUserId, sanitizedFile),
                        storageService.uploadRaceFile(currentRaceData.id, currentUserId, rawFile)
                    ]);

                    fileUrl = cleanUrl;
                    rawFileUrl = rawUrl;

                    const athleteIndex = finalAthletes.findIndex(a => a.id === newAthleteFinal.id);
                    if (athleteIndex !== -1) {
                        finalAthletes[athleteIndex].fileUrl = fileUrl;
                        finalAthletes[athleteIndex].rawFileUrl = rawFileUrl;
                    }

                    // Also add it to the user's activities subcollection
                    try {
                        const activityId = `manual_${Date.now()}`;
                        const startTimestamp = newAthleteFinal.track[0].timestamp;
                        const endTimestamp = newAthleteFinal.track[newAthleteFinal.track.length - 1].timestamp;
                        const distanceMeters = newAthleteFinal.track[newAthleteFinal.track.length - 1].distance;

                        await setDoc(doc(db, 'users', currentUserId, 'activities', activityId), {
                            source: 'manual',
                            externalId: activityId,
                            name: newAthleteFinal.name || file.name,
                            startTime: startTimestamp,
                            distance: distanceMeters,
                            duration: Math.round((endTimestamp - startTimestamp) / 1000), // in seconds
                            fileUrl: fileUrl,
                            rawFileUrl: rawFileUrl,
                            createdAt: serverTimestamp()
                        });

                        if (userFtp > 0) {
                            try {
                                const profile = await userService.getUserProfile(currentUserId);
                                const currentCount = profile?.racesAnalyzedForMatches || 0;
                                const recentMatches: number[] = profile?.recentRaceMatches || [];
                                recentMatches.push(newAthleteFinal.matchEvents?.length || 0);

                                if (recentMatches.length > 5) {
                                    recentMatches.shift();
                                }

                                const updatePayload: any = {
                                    racesAnalyzedForMatches: currentCount + 1,
                                    recentRaceMatches: recentMatches
                                };

                                if (currentCount + 1 >= 5) {
                                    updatePayload.estimatedMatches = Math.max(...recentMatches);
                                }

                                await userService.updateUserProfile(currentUserId, updatePayload);
                            } catch (calErr) {
                                console.error("Failed to update calibration count:", calErr);
                            }
                        }
                    } catch (actErr) {
                        console.error("Failed to log manual activity to user profile:", actErr);
                    }
                } catch (e) {
                    console.warn("Failed to upload file to storage, proceeding with local processing only.", e);
                }
            }

            // --- FINAL RACE DATA CONSTRUCTION ---

            const isZeroPoint = (p: any) => {
                if (!p) return true;
                if (Array.isArray(p)) return p[0] === 0 && p[1] === 0;
                const lat = p.lat ?? p.latitude ?? 0;
                const lon = p.lon ?? p.lng ?? p.longitude ?? 0;
                return lat === 0 && lon === 0;
            };

            const existingStart = existingCourse?.start;
            const hasManualStart = !isZeroPoint(existingStart);

            const existingFinish = existingCourse?.finish;
            const hasManualFinish = !isZeroPoint(existingFinish);

            // If this is the first athlete or no course exists, their path becomes the course path.
            const coursePath = (currentRaceData.athletes.length === 0 || !existingCourse) ? newPath : existingCourse.path;

            const consensusStart = findConsensusCoursePoint(finalAthletes, 'start');
            const consensusFinish = findConsensusCoursePoint(finalAthletes, 'finish');

            const course: Course = {
                path: coursePath,
                start: hasManualStart ? existingStart! : (consensusStart || [0, 0]),
                finish: hasManualFinish ? existingFinish! : (consensusFinish || [0, 0]),
            };

            const updatedRaceData: AthleteUploadContext = {
                ...currentRaceData,
                course,
                athletes: finalAthletes,
                duration,
                startTime: finalAthletes.length >= 6 ? raceStartTime : 0
            };

            resolve(updatedRaceData);

        } catch (error) {
            reject(error);
        }
    });
};
